// =============================================================================
// Concierge AI — Admin Chat Widget
// =============================================================================
// Floating chat panel for business owners to test their AI assistant.
// Uses POST /api/chat/:tenantSlug to send messages and get responses.
// =============================================================================

import { useState, useRef, useEffect, useCallback } from "react";
import {
  MessageSquare,
  X,
  Send,
  Bot,
  User,
  Loader2,
  Minimize2,
  Maximize2,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useToast } from "@/components/ui/toast";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
}

interface ChatState {
  messages: ChatMessage[];
  sessionId: string | null;
  loading: boolean;
  expanded: boolean;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : "/api";

async function fetchChatInfo(slug: string): Promise<{
  tenantName: string;
  domain: string;
  assistantName: string;
  greeting: string;
}> {
  const res = await fetch(`${API_BASE}/chat/${encodeURIComponent(slug)}/info`);
  if (!res.ok) throw new Error("Failed to load chat info");
  return res.json();
}

async function sendChatMessage(
  slug: string,
  message: string,
  sessionId: string | null,
): Promise<{ reply: string; sessionId: string; intent?: string }> {
  const res = await fetch(`${API_BASE}/chat/${encodeURIComponent(slug)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, sessionId }),
  });
  if (!res.ok) throw new Error("Failed to send message");
  return res.json();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminChatWidget() {
  const tenant = useAuthStore((s) => s.tenant);
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ChatState>({
    messages: [],
    sessionId: null,
    loading: false,
    expanded: false,
  });
  const [input, setInput] = useState("");
  const [assistantName, setAssistantName] = useState("Concierge AI");
  const [initialized, setInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build tenant slug from name
  const tenantSlug = tenant?.name
    ? tenant.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    : tenant?.id ?? "";

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state.messages]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Initialize chat info on first open
  const initChat = useCallback(async () => {
    if (initialized || !tenantSlug) return;
    try {
      const info = await fetchChatInfo(tenantSlug);
      setAssistantName(info.assistantName);
      setState((prev) => ({
        ...prev,
        messages: [
          {
            id: "greeting",
            role: "assistant",
            content: info.greeting,
            timestamp: Date.now(),
          },
        ],
      }));
      setInitialized(true);
    } catch {
      setState((prev) => ({
        ...prev,
        messages: [
          {
            id: "greeting",
            role: "system",
            content: `Hi! Welcome to ${tenant?.name ?? "your business"}. How can I help you today?`,
            timestamp: Date.now(),
          },
        ],
      }));
      setInitialized(true);
    }
  }, [initialized, tenantSlug, tenant?.name]);

  const handleOpen = () => {
    setOpen(true);
    initChat();
  };

  // Send message
  const handleSend = async () => {
    const text = input.trim();
    if (!text || state.loading || !tenantSlug) return;

    const userMsg: ChatMessage = {
      id: `u_${Date.now()}`,
      role: "user",
      content: text,
      timestamp: Date.now(),
    };

    setInput("");
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMsg],
      loading: true,
    }));

    try {
      const res = await sendChatMessage(tenantSlug, text, state.sessionId);
      const assistantMsg: ChatMessage = {
        id: `a_${Date.now()}`,
        role: "assistant",
        content: res.reply,
        timestamp: Date.now(),
      };
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, assistantMsg],
        sessionId: res.sessionId,
        loading: false,
      }));
    } catch (err) {
      toast("error", "Failed to get response. Is the worker running?");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleReset = () => {
    setState({
      messages: [],
      sessionId: null,
      loading: false,
      expanded: state.expanded,
    });
    setInitialized(false);
    initChat();
  };

  const toggleExpand = () => {
    setState((prev) => ({ ...prev, expanded: !prev.expanded }));
  };

  if (!tenant) return null;

  // Floating button
  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary-dark transition-colors px-4 py-3"
        aria-label="Test your AI assistant"
      >
        <MessageSquare className="h-5 w-5" />
        <span className="text-sm font-medium hidden sm:inline">Test Assistant</span>
      </button>
    );
  }

  // Chat panel
  return (
    <div
      className={cn(
        "fixed z-50 flex flex-col bg-background border border-border rounded-xl shadow-2xl transition-all duration-200",
        state.expanded
          ? "bottom-4 right-4 left-4 top-4 md:left-auto md:top-4 md:w-[560px]"
          : "bottom-6 right-6 w-[380px] h-[560px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5 rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground">
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold">{assistantName}</p>
            <p className="text-[10px] text-muted-foreground">Preview — see what your customers experience</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleReset}
            aria-label="Reset conversation"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={toggleExpand}
            aria-label={state.expanded ? "Minimize" : "Maximize"}
          >
            {state.expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setOpen(false)}
            aria-label="Close chat"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
        {state.messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "flex gap-2",
              msg.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            {msg.role !== "user" && (
              <div className="flex items-start justify-center h-7 w-7 rounded-full bg-primary/10 shrink-0 mt-0.5">
                <Bot className="h-3.5 w-3.5 text-primary mt-1.5" />
              </div>
            )}
            <div
              className={cn(
                "max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed",
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : msg.role === "system"
                    ? "bg-muted text-foreground rounded-bl-md"
                    : "bg-muted text-foreground rounded-bl-md",
              )}
            >
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
            </div>
            {msg.role === "user" && (
              <div className="flex items-start justify-center h-7 w-7 rounded-full bg-primary/20 shrink-0 mt-0.5">
                <User className="h-3.5 w-3.5 text-primary mt-1.5" />
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {state.loading && (
          <div className="flex gap-2 justify-start">
            <div className="flex items-start justify-center h-7 w-7 rounded-full bg-primary/10 shrink-0 mt-0.5">
              <Bot className="h-3.5 w-3.5 text-primary mt-1.5" />
            </div>
            <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border p-3">
        <form
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="flex items-center gap-2"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type as a customer would..."
            className="flex-1 rounded-full border border-input bg-background px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring transition-shadow"
            disabled={state.loading}
            maxLength={2000}
            aria-label="Test message as customer"
          />
          <Button
            type="submit"
            size="icon"
            className="h-9 w-9 rounded-full shrink-0"
            disabled={!input.trim() || state.loading}
            aria-label="Send message"
          >
            {state.loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
        <p className="text-[10px] text-muted-foreground text-center mt-1.5">
          Testing as {tenant?.name ?? "your business"} • Powered by Concierge AI
        </p>
      </div>
    </div>
  );
}
